// ui.ts
// Shared interface text (header, bottom bar, consent pop-up), English and
// German side by side. Slide content lives in each slide's slideN.data.ts.
// The German object must have exactly the same shape as the English one.
import { ByLang, useL } from "./lang";

const en = {
  header: {
    title: "Dreamfulness",
    logo: "Yogyism",
    support: "Support US",
    supportMenu: "Support Us",
    contact: "Contact Us",
    session: "Session",
    login: "Login",
    logout: "Logout",
    menu: "Menu",
    language: "Language",
  },
  consent: {
    title: "Before you login",
    intro: [
      "This app is an experimental, personal project exploring sleep and dream sensing — built for learning and exploration, not as a medical product.",
      "It plays gentle audio cues during your sleep based on sensor readings. Since this is still experimental, detection won't always be perfect. If you ever feel uncomfortable, you can stop at any time.",
      "This is meant for personal experimentation and isn't a substitute for professional medical or mental health care.",
    ],
    showDetails: "Read full details ▾",
    hideDetails: "Hide full details ▲",
    details: [
      { title: "What this is", body: "An independent, personal engineering project exploring sensor data collection, signal processing, and audio playback triggered by detected patterns — built purely for learning and experimentation." },
      { title: "Not a medical device", body: "Not a certified medical device, and not intended to diagnose, treat, cure, or prevent any medical or psychological condition." },
      { title: "Your own risk, your own choice", body: "Use of this app is voluntary. If you ever feel distressed or unwell, stop using the app and reach out to a trusted person or professional." },
      { title: "No warranty", body: 'Provided "as is," without warranty of any kind. The developer(s) accept no liability for outcomes from its use.' },
    ],
    checkbox: "I understand this is an experimental personal project, and I'm happy to continue at my own discretion.",
    decline: "Not now",
    accept: "Continue to login →",
  },
  tutorial: {
    // Bottom bar labels, in slide order (Dreamfulness … Session).
    slideLabels: [
      "Dreamfulness", "Mandukya", "Mind States", "Dream State", "Memory & Dreams",
      "Psychosomatic", "Sleep Cycle", "Nostril & Sleep", "Brain Waves", "Mind Body",
      "8 Limbs", "Adhama", "Nadi Shuddhi", "Chandra Bhedana", "Bhramari",
      "Meditation", "Yoga Nidra", "Session",
    ],
    modeAll: "All",
    modePractice: "Practice",
    modePracticeShort: "Prac.",
    prev: "Prev",
    next: "Next",
    previousAria: "Previous",
    nextAria: "Next",
    footerShort: "Dreamfulness- By Yogyism.",
    footerLong: "Dreamfulness- By Yogyism. All rights reserved.",
  },
};

export type UiText = typeof en;

const de: UiText = {
  header: {
    title: "Dreamfulness",
    logo: "Yogyism",
    support: "Unterstützen",
    supportMenu: "Unterstützen",
    contact: "Kontakt",
    session: "Sitzung",
    login: "Anmelden",
    logout: "Abmelden",
    menu: "Menü",
    language: "Sprache",
  },
  consent: {
    title: "Bevor du dich anmeldest",
    intro: [
      "Diese App ist ein experimentelles, persönliches Projekt, das Schlaf- und Traumerkennung erforscht – entwickelt zum Lernen und Ausprobieren, nicht als medizinisches Produkt.",
      "Sie spielt während deines Schlafs sanfte Audio-Hinweise ab, basierend auf Sensormesswerten. Da sie noch experimentell ist, wird die Erkennung nicht immer perfekt sein. Wenn du dich unwohl fühlst, kannst du jederzeit aufhören.",
      "Sie ist für persönliches Experimentieren gedacht und ersetzt keine professionelle medizinische oder psychologische Betreuung.",
    ],
    showDetails: "Alle Details lesen ▾",
    hideDetails: "Details ausblenden ▲",
    details: [
      { title: "Was das ist", body: "Ein unabhängiges, persönliches Technikprojekt, das Sensordatenerfassung, Signalverarbeitung und Audiowiedergabe bei erkannten Mustern erforscht – rein zum Lernen und Experimentieren entwickelt." },
      { title: "Kein Medizinprodukt", body: "Kein zertifiziertes Medizinprodukt und nicht dazu bestimmt, medizinische oder psychische Erkrankungen zu diagnostizieren, zu behandeln, zu heilen oder ihnen vorzubeugen." },
      { title: "Eigenes Risiko, eigene Entscheidung", body: "Die Nutzung dieser App ist freiwillig. Wenn du dich belastet oder unwohl fühlst, beende die Nutzung und wende dich an eine Vertrauensperson oder eine Fachkraft." },
      { title: "Keine Gewährleistung", body: "Bereitgestellt „wie besehen“, ohne jegliche Gewährleistung. Die Entwickler übernehmen keine Haftung für Folgen der Nutzung." },
    ],
    checkbox: "Ich verstehe, dass dies ein experimentelles, persönliches Projekt ist, und fahre gern nach eigenem Ermessen fort.",
    decline: "Jetzt nicht",
    accept: "Weiter zur Anmeldung →",
  },
  tutorial: {
    slideLabels: [
      "Dreamfulness", "Mandukya", "Geisteszustände", "Traumzustand", "Gedächtnis & Träume",
      "Psychosomatik", "Schlafzyklus", "Nasenatmung & Schlaf", "Gehirnwellen", "Geist & Körper",
      "8 Glieder", "Adhama", "Nadi Shuddhi", "Chandra Bhedana", "Bhramari",
      "Meditation", "Yoga Nidra", "Sitzung",
    ],
    modeAll: "Alle",
    modePractice: "Praxis",
    modePracticeShort: "Praxis",
    prev: "Zurück",
    next: "Weiter",
    previousAria: "Zurück",
    nextAria: "Weiter",
    footerShort: "Dreamfulness – von Yogyism.",
    footerLong: "Dreamfulness – von Yogyism. Alle Rechte vorbehalten.",
  },
};

export const uiText: ByLang<UiText> = { en, de };
export const useUi = () => useL(uiText);
